<script setup>
// Inspector do nó "Enviar mídia": biblioteca de mídias que o Agente pode
// mandar. Você sobe as imagens/arquivos (ou cola uma URL) e dá um nome a cada
// um; o Agente escolhe qual enviar pela descrição. Guarda em data.items =
// [{ id, label, url }]. O backend lista essas mídias na descrição da ferramenta
// pro LLM passar a url certa.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import mediaAPI from 'dashboard/api/agents/media';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const items = computed(() =>
  Array.isArray(props.data.items) ? props.data.items : []
);
const uploading = ref(false);
const fileInput = ref(null);
const urlDraft = ref('');

const genId = () => `m_${Date.now().toString(36)}`;
const isImage = url => /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(url || '');

const commit = next => emit('update', { items: next });

const updateLabel = (id, label) =>
  commit(items.value.map(it => (it.id === id ? { ...it, label } : it)));

const removeItem = id => commit(items.value.filter(it => it.id !== id));

const pickFile = () => fileInput.value?.click();

const onFile = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  uploading.value = true;
  try {
    const { data } = await mediaAPI.upload(file);
    const url = data.file_url;
    if (!url) throw new Error('no url');
    commit([
      ...items.value,
      { id: genId(), label: file.name.replace(/\.[^.]+$/, ''), url },
    ]);
  } catch {
    useAlert(t('AI_AGENTS.FLOW.MEDIA.UPLOAD_ERROR'));
  } finally {
    uploading.value = false;
  }
};

const addUrl = () => {
  const url = urlDraft.value.trim();
  if (!url) return;
  commit([...items.value, { id: genId(), label: '', url }]);
  urlDraft.value = '';
};
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-11"
        >
          <span class="i-lucide-image text-base" />
        </span>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.MEDIA.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.MEDIA.HINT') }}
    </p>

    <!-- Liga/desliga -->
    <label
      class="mb-3 flex cursor-pointer items-center justify-between rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED') }}
      </span>
      <input
        type="checkbox"
        class="size-4 shrink-0 accent-[#4285f4]"
        :checked="data.enabled !== false"
        @change="emit('update', { enabled: $event.target.checked })"
      />
    </label>

    <!-- Lista de mídias -->
    <div class="space-y-2">
      <div
        v-for="it in items"
        :key="it.id"
        class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-2"
      >
        <img
          v-if="isImage(it.url)"
          :src="it.url"
          class="size-10 shrink-0 rounded-md object-cover"
          alt=""
        />
        <span
          v-else
          class="flex size-10 shrink-0 items-center justify-center rounded-md bg-n-alpha-2 text-n-slate-11"
        >
          <span class="i-lucide-file text-base" />
        </span>
        <input
          :value="it.label"
          :placeholder="t('AI_AGENTS.FLOW.MEDIA.LABEL_PH')"
          class="min-w-0 flex-1 rounded-md border border-n-weak bg-n-solid-2 px-2 py-1.5 text-sm text-n-slate-12"
          @input="updateLabel(it.id, $event.target.value)"
        />
        <button
          v-tooltip="t('AI_AGENTS.FLOW.MEDIA.REMOVE')"
          class="i-lucide-trash-2 shrink-0 text-n-ruby-9"
          @click="removeItem(it.id)"
        />
      </div>

      <p
        v-if="!items.length"
        class="rounded-lg border border-dashed border-n-weak px-3 py-4 text-center text-xs text-n-slate-10"
      >
        {{ t('AI_AGENTS.FLOW.MEDIA.EMPTY') }}
      </p>
    </div>

    <!-- Adicionar -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*,audio/*,video/*,application/pdf"
      class="hidden"
      @change="onFile"
    />
    <button
      class="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-n-brand py-2 text-sm font-medium text-n-brand hover:bg-n-brand/10 disabled:opacity-60"
      :disabled="uploading"
      @click="pickFile"
    >
      <span
        :class="
          uploading ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-upload'
        "
      />
      {{
        uploading
          ? t('AI_AGENTS.FLOW.MEDIA.UPLOADING')
          : t('AI_AGENTS.FLOW.MEDIA.ADD')
      }}
    </button>

    <div class="mt-2 flex items-center gap-2">
      <input
        v-model="urlDraft"
        :placeholder="t('AI_AGENTS.FLOW.MEDIA.URL_PH')"
        class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-1.5 text-sm"
        @keydown.enter="addUrl"
      />
      <button
        class="shrink-0 rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-slate-11 hover:bg-n-solid-2"
        @click="addUrl"
      >
        {{ t('AI_AGENTS.FLOW.MEDIA.ADD_URL') }}
      </button>
    </div>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
